import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, ButtonGroup, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { trackPromise } from 'react-promise-tracker';
import Swal from 'sweetalert2';
import moment from 'moment';
import caAPI from '../../../services/caAPI';
import DommusBarraTitulo from '../../../components/DommusBarraTitulo';
import ItemBarraTitulo from '../../../components/DommusBarraTitulo/ItemBarraTitulo';
import DommusTabelaPaginadaControlada from '../../../components/DommusTabelaPaginadaControlada';
import { successToast } from '../../../components/DommusToast';
import { extrairDados, obterMensagemErro } from '../GestaoAgentesIa/utils';
import ModalBaseConhecimento from './ModalBaseConhecimento';
import ConfiguracaoOpenAi from './ConfiguracaoOpenAi';

export default function BasesConhecimento() {
  const [listaBases, setListaBases] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(15);
  const [totalBases, setTotalBases] = useState(0);
  const [erroCarregamento, setErroCarregamento] = useState(false);
  const [openModalBase, setOpenModalBase] = useState(false);
  const [idBaseSelecionada, setIdBaseSelecionada] = useState(null);
  const [configuracaoOpenAi, setConfiguracaoOpenAi] = useState(null);

  const chaveVectorStoreConfigurada = Boolean(configuracaoOpenAi?.chave_vector_store_configurada);
  const tooltipChaveNaoConfigurada = useMemo(() => (
    <Tooltip id="tooltip-chave-vector-store-nao-configurada">
      Configure a chave de Vector Store antes de excluir Bases de Conhecimento.
    </Tooltip>
  ), []);

  const handleCarregarBases = useCallback(() => {
    return trackPromise(
      caAPI.baseConhecimento.list({ page: pagina, per_page: tamanhoPagina })
        .then(resposta => {
          setListaBases(extrairDados(resposta));
          setTotalBases(resposta?.data?.total || 0);
          setErroCarregamento(false);
        })
        .catch(() => setErroCarregamento(true))
    );
  }, [pagina, tamanhoPagina]);

  useEffect(() => {
    handleCarregarBases();
  }, [handleCarregarBases]);

  function handleAlterarTamanhoPagina(novoTamanho) {
    setPagina(1);
    setTamanhoPagina(Number(novoTamanho) || 15);
  }

  function handleAbrirNovaBase() {
    setIdBaseSelecionada(null);
    setOpenModalBase(true);
  }

  function handleEditarBase(base) {
    setIdBaseSelecionada(base.id_base_conhecimento);
    setOpenModalBase(true);
  }

  function handleFecharModalBase() {
    setOpenModalBase(false);
    setIdBaseSelecionada(null);
  }

  function handleBaseSalva() {
    handleFecharModalBase();
    handleCarregarBases();
  }

  function handleExcluirBase(base) {
    if (!chaveVectorStoreConfigurada) return;

    Swal.fire({
      titleText: 'Excluir Base de Conhecimento?',
      text: `Deseja excluir a Base "${base.nome}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    }).then(resultado => {
      if (!resultado.isConfirmed) return;

      trackPromise(
        caAPI.baseConhecimento.delete(base.id_base_conhecimento)
          .then(() => {
            successToast.fire({ text: 'Base de Conhecimento excluída com sucesso.' });
            if (listaBases.length === 1 && pagina > 1) {
              setPagina(pagina - 1);
              return;
            }
            handleCarregarBases();
          })
          .catch(erro => {
            Swal.fire({
              titleText: 'Não foi possível excluir a Base',
              text: obterMensagemErro(erro, 'Erro ao excluir Base de Conhecimento.'),
              icon: 'error',
            });
          })
      );
    });
  }

  const cabecalhoTabela = (
    <tr>
      <th>Nome</th>
      <th>Arquivos</th>
      <th>Agentes vinculados</th>
      <th>Criada em</th>
      <th className="text-center">Ações</th>
    </tr>
  );

  const linhasTabela = listaBases.map(base => {
    const botaoExcluir = (
      <Button type="button" variant="outline-danger" title="Excluir Base" aria-label={`Excluir ${base.nome}`} onClick={() => handleExcluirBase(base)} disabled={!chaveVectorStoreConfigurada} style={!chaveVectorStoreConfigurada ? { pointerEvents: 'none' } : undefined}>
        <FontAwesomeIcon icon={faTrash} />
      </Button>
    );

    return (
      <tr key={base.id_base_conhecimento}>
        <td>
          {base.nome}
          {base.ativo === false && <span className="badge badge-secondary ml-2">Inativa</span>}
        </td>
        <td>{base.total_arquivos}</td>
        <td>{base.agentes?.length ? base.agentes.map(agente => agente.nome).join(', ') : 'Nenhum'}</td>
        <td>{base.criado_em ? moment(base.criado_em).format('DD/MM/YYYY HH:mm') : '-'}</td>
        <td className="text-center">
          <ButtonGroup size="sm">
            <Button type="button" variant="outline-primary" title="Editar Base" aria-label={`Editar ${base.nome}`} onClick={() => handleEditarBase(base)}>
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            {chaveVectorStoreConfigurada ? botaoExcluir : (
              <OverlayTrigger overlay={tooltipChaveNaoConfigurada} placement="top">
                <span className="d-inline-block">{botaoExcluir}</span>
              </OverlayTrigger>
            )}
          </ButtonGroup>
        </td>
      </tr>
    );
  });

  return (
    <Container fluid>
      <DommusBarraTitulo>
        <ItemBarraTitulo>Conhecimento para IA</ItemBarraTitulo>
        <OverlayTrigger overlay={<Tooltip id="tooltip-nova-base">Criar nova Base</Tooltip>} placement="top">
          <ItemBarraTitulo classe="btn py-3" onClick={handleAbrirNovaBase}>
            <FontAwesomeIcon icon={faPlusCircle} color="#4caf50" size="lg" />
          </ItemBarraTitulo>
        </OverlayTrigger>
      </DommusBarraTitulo>

      <ConfiguracaoOpenAi configuracao={configuracaoOpenAi} setConfiguracao={setConfiguracaoOpenAi} />

      <div className="mt-3">
        {erroCarregamento ? (
          <Alert variant="danger" className="mb-0">
            Não foi possível carregar as Bases de Conhecimento. <Button variant="link" className="p-0" onClick={handleCarregarBases}>Tentar novamente</Button>.
          </Alert>
        ) : listaBases.length ? (
          <DommusTabelaPaginadaControlada
            linhasCabecalho={cabecalhoTabela}
            linhasCorpo={linhasTabela}
            optionsTabela={{}}
            pagina={pagina}
            setPagina={setPagina}
            tamanhoPagina={tamanhoPagina}
            setTamanhoPagina={handleAlterarTamanhoPagina}
            tamanhoTotal={totalBases}
          />
        ) : (
          <Alert variant="info" className="mb-0">Nenhuma Base de Conhecimento cadastrada. Clique em 'Nova Base' para começar.</Alert>
        )}
      </div>

      <ModalBaseConhecimento
        aberto={openModalBase}
        close={handleFecharModalBase}
        idBaseConhecimento={idBaseSelecionada}
        onSalvo={handleBaseSalva}
        chaveVectorStoreConfigurada={chaveVectorStoreConfigurada}
      />
    </Container>
  );
}
